import React, {useCallback, useEffect, useState} from 'react';
import {View, StyleSheet, Alert} from 'react-native';
import {
  Text,
  ActivityIndicator,
  Dialog,
  Portal,
  Button,
  TextInput,
  IconButton,
  Snackbar,
  Divider,
  FAB,
} from 'react-native-paper';
import DraggableFlatList, {
  ScaleDecorator,
} from 'react-native-draggable-flatlist';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {useTaskTypeStore} from '../../store/useTaskTypeStore';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const TaskTypeListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {
    taskTypes,
    isLoading,
    fetchTaskTypes,
    createTaskType,
    updateTaskType,
    deleteTaskType,
    reorderTaskTypes,
  } = useTaskTypeStore();

  const [refreshing, setRefreshing] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [taskName, setTaskName] = useState('');
  const [taskDays, setTaskDays] = useState('');
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    fetchTaskTypes();
  }, [fetchTaskTypes]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchTaskTypes();
    setRefreshing(false);
  }, [fetchTaskTypes]);

  const openAddDialog = useCallback(() => {
    setEditingItem(null);
    setTaskName('');
    setTaskDays('');
    setDialogVisible(true);
  }, []);

  const openEditDialog = useCallback(item => {
    setEditingItem(item);
    setTaskName(item.name);
    setTaskDays(String(item.durationDays));
    setDialogVisible(true);
  }, []);

  const handleSave = useCallback(async () => {
    const name = taskName.trim();
    const days = parseInt(taskDays, 10);
    if (!name || !days || days < 1) return;

    try {
      if (editingItem) {
        await updateTaskType(editingItem.id, {name, durationDays: days});
      } else {
        const code = name.toUpperCase().replace(/[^A-Z0-9]+/g, '_');
        await createTaskType({name, code, durationDays: days});
      }
      setDialogVisible(false);
      setTaskName('');
      setTaskDays('');
      setEditingItem(null);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    }
  }, [taskName, taskDays, editingItem, createTaskType, updateTaskType, t]);

  const handleDelete = useCallback(
    item => {
      Alert.alert(t('common.delete'), t('taskType.deleteConfirm'), [
        {text: t('common.cancel'), style: 'cancel'},
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteTaskType(item.id);
            } catch (e) {
              setSnackbar(
                e.response?.data?.error?.message || t('common.error'),
              );
            }
          },
        },
      ]);
    },
    [deleteTaskType, t],
  );

  const handleDragEnd = useCallback(
    async ({data}) => {
      const orderedIds = data.map(item => item.id);
      try {
        await reorderTaskTypes(orderedIds);
      } catch (e) {
        setSnackbar(e.response?.data?.error?.message || t('common.error'));
      }
    },
    [reorderTaskTypes, t],
  );

  const formatDays = days => {
    return days === 1
      ? `${days} ${t('taskType.day')}`
      : `${days} ${t('taskType.days')}`;
  };

  const renderItem = useCallback(
    ({item, drag, isActive}) => {
      return (
        <ScaleDecorator>
          <View
            style={[
              styles.row,
              isActive && styles.rowDragging,
            ]}>
            <IconButton
              icon="drag"
              size={22}
              iconColor={colors.textSecondary}
              onLongPress={drag}
              delayLongPress={150}
              style={styles.dragHandle}
            />

            <Text style={styles.taskName}>{item.name}</Text>

            <View style={styles.rowActions}>
              <Text style={styles.daysText}>
                {formatDays(item.durationDays)}
              </Text>
              <IconButton
                icon="pencil-outline"
                size={20}
                iconColor={colors.textSecondary}
                onPress={() => openEditDialog(item)}
                style={styles.actionBtn}
              />
              <IconButton
                icon="delete-outline"
                size={20}
                iconColor={colors.textSecondary}
                onPress={() => handleDelete(item)}
                style={styles.actionBtn}
              />
            </View>
          </View>
        </ScaleDecorator>
      );
    },
    [openEditDialog, handleDelete, t],
  );

  const keyExtractor = useCallback(item => String(item.id), []);

  const ListHeader = useCallback(
    () => (
      <View>
        <View style={styles.descriptionRow}>
          <Text style={styles.descriptionText}>
            {t('taskType.description')}
          </Text>
        </View>
        <Divider />
      </View>
    ),
    [t],
  );

  const ListEmpty = useCallback(
    () => <Text style={styles.emptyText}>{t('common.noData')}</Text>,
    [t],
  );

  const ItemSeparator = useCallback(() => <Divider />, []);

  if (isLoading && taskTypes.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader
        title={t('taskType.title')}
        onBack={() => navigation.goBack()}
      />

      <DraggableFlatList
        data={taskTypes}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        onDragEnd={handleDragEnd}
        ListHeaderComponent={ListHeader}
        ListEmptyComponent={ListEmpty}
        ItemSeparatorComponent={ItemSeparator}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        activationDistance={0}
      />

      {/* Add / Edit Dialog */}
      <Portal>
        <Dialog
          visible={dialogVisible}
          onDismiss={() => setDialogVisible(false)}
          style={{borderRadius: 15}}>
          <Dialog.Title>
            {editingItem ? t('taskType.editTask') : t('taskType.addTask')}
          </Dialog.Title>
          <Dialog.Content>
            <TextInput
              label={t('taskType.taskName')}
              value={taskName}
              onChangeText={setTaskName}
              mode="outlined"
              style={styles.dialogInput}
            />
            <TextInput
              label={t('taskType.daysLabel')}
              value={taskDays}
              onChangeText={setTaskDays}
              mode="outlined"
              keyboardType="number-pad"
              style={[styles.dialogInput, styles.daysInput]}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleSave}
              style={styles.saveButton}>
              {t('taskType.saveTask')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <FAB
        icon="plus"
        style={styles.fab}
        onPress={openAddDialog}
        color={colors.white}
      />

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  descriptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: colors.surface,
  },
  descriptionText: {
    flex: 1,
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  fab: {
    position: 'absolute',
    right: spacing.md,
    bottom: spacing.md,
    backgroundColor: colors.primary,
  },
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    paddingVertical: spacing.xs,
    paddingRight: spacing.xs,
  },
  rowDragging: {
    backgroundColor: colors.background,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.25,
    shadowRadius: 4,
  },
  dragHandle: {
    margin: 0,
    marginLeft: spacing.xs,
  },
  taskName: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginLeft: spacing.xs,
  },
  rowActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  daysText: {
    fontSize: 14,
    color: colors.textSecondary,
    marginRight: spacing.xs,
  },
  actionBtn: {
    margin: 0,
  },
  dialogInput: {
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
  },
  daysInput: {
    width: 120,
  },
  saveButton: {
    backgroundColor: colors.primary,
    borderRadius: 10,
    paddingHorizontal: 10,
  },
});

export default TaskTypeListScreen;
